/**
 * Fit-stage fields every family shares (plan §4.3; FND-08, FND-13): what the piece stands on and
 * how its outer sides are made, read from the photo as proportions and turned into the flat
 * parameter keys of src/families/base.ts.
 */
import { z } from 'zod';
import { mm, toMm } from '../domain/units';
import { PLINTH, SLATS, TOE, type BaseKind, type BaseParams, type SideParams } from './base';

export const BaseFitShape = {
  base: z.enum(['floor', 'plinth', 'sides', 'feet', 'legs', 'unknown']).default('unknown')
    .describe('What the bottom stands on: floor (the bottom board itself lies on the floor, no gap under it); plinth (a solid kick board or recessed base fills the space under the bottom, often set back); sides (the outer side panels run down past the bottom board to the floor and act as feet, leaving an open gap under the bottom, often with a shaped cut-out or short integral feet at the corners); feet (short SEPARATE feet, levelling feet or bun feet fixed under the piece, under about 40 mm); legs (taller separate legs); unknown. If there is any visible gap under the bottom board, it is not floor.'),
  baseHeightOverHeight: z.number().min(0).max(1).default(0).describe('Plinth, gap under the bottom (sides), feet or legs height divided by the overall height; 0 when it stands on the floor.'),
  plinthSetbackOverDepth: z.number().min(0).max(1).default(0).describe('How far a plinth\'s front face is set back behind the front edge, over the depth; 0 when flush or when there is no plinth.'),
};

export const SideFitShape = {
  sides: z.enum(['panel', 'slatted', 'unknown']).default('panel').describe('The two outer sides: panel (solid boards); slatted (rows of vertical slats with gaps you can see through); unknown.'),
  slatsPerSide: z.int().min(0).default(0).describe('Vertical members in one outer side, counted front to back including the front and back edges; 0 unless the sides are slatted.'),
};

/**
 * Doors and drawers on the front (src/families/fronts.ts), read as boxes over the front elevation: each box is
 * mapped to the openings it covers once the design is built (fit.ts `frontsFromFit`).
 */
export const FrontsFitShape = {
  // kept terse: the stage-2 schema has a size budget; FRONTS_GUIDE (in the prompt) explains the fields
  fronts: z.array(z.object({
    box: z.array(z.number()).describe('[xStart, xEnd, yStart, yEnd]'),
    kind: z.enum(['door', 'glazed', 'sliding', 'drawers']),
    mount: z.enum(['overlay', 'inset']),
    hinge: z.enum(['left', 'right', 'pair', 'none']),
    drawers: z.int().min(0).max(4),
  })).default([]).describe('Doors and drawers as boxes over the front, fractions of W and H (y from the floor); [] when open.'),
};
export const FRONTS_GUIDE = 'Fronts: list every door, pair of doors, glazed door, set of sliding doors and stack of drawers as the box it covers on the front (box: [xStart, xEnd, yStart, yEnd], x as fractions of the overall width, y as fractions of the height from the floor). kind: door (solid, hinged), glazed (hinged with glass), sliding, drawers. mount: overlay when the front covers the case edges around it, inset when it sits inside the opening with the case edges showing round it. hinge: the side a single door hangs from (its handle is on the other side), pair for two doors meeting in the middle, none for drawers and sliding doors. drawers: how many are stacked in the box (0 for doors). Give [] when every opening is open.';

export interface BaseFit { base: 'floor' | 'plinth' | 'sides' | 'feet' | 'legs' | 'unknown'; baseHeightOverHeight: number; plinthSetbackOverDepth: number }
export interface SideFit { sides: 'panel' | 'slatted' | 'unknown'; slatsPerSide: number }

const r5 = (v: number) => Math.round(v / 50) * 50;

/**
 * The base keys from a fitted block (H, D in ticks). Sides running to the floor keep the gap
 * under the raised bottom (20–250 mm). Feet and legs become the kit's levelling
 * feet (legs taller than a foot are reported as unmodelled by the fidelity check); a plinth keeps
 * its height (30–150 mm) and set-back. Older fits with only `plinth: 'yes'` read as a plinth.
 */
export function baseFromFit(f: Partial<BaseFit> & { plinth?: string; plinthOverHeight?: number } | null | undefined, H: number, D: number): BaseParams {
  if (!f) return {};
  const kind: BaseKind | null = f.base === 'plinth' || (!f.base && f.plinth === 'yes') ? 'plinth' : f.base === 'sides' ? 'sides' : f.base === 'feet' || f.base === 'legs' ? 'feet' : f.base === 'floor' ? 'none' : null;
  if (kind === 'sides') {
    const ratio = f.baseHeightOverHeight || 0.035;
    return { base: 'sides', toeHeight: Math.max(TOE.min, Math.min(TOE.max, mm(Math.round((toMm(H) * ratio) / 5) * 5))) };
  }
  if (kind === 'plinth') {
    const ratio = f.baseHeightOverHeight || f.plinthOverHeight || 0.04;
    const height = Math.max(PLINTH.min, Math.min(PLINTH.max, mm(Math.round((toMm(H) * ratio) / 5) * 5)));
    const setback = Math.max(0, Math.min(PLINTH.maxSetback, Math.max(0, D - mm(100)), r5(D * (f.plinthSetbackOverDepth ?? 0))));
    return { base: 'plinth', plinth: height, ...(setback >= mm(5) ? { plinthSetback: setback } : {}) };
  }
  // feet take their kind's own height (levelling feet under ply, turned 100 mm feet under solid timber)
  if (kind === 'feet') return { base: 'feet' };
  return {};
}

/**
 * Slat width and gap from the counted slats (depth D in ticks). The side is a frame of two stiles
 * (one slat wide) with the slats between: n + 2 members and n + 1 gaps of half a slat across D.
 */
export function sidesFromFit(f: Partial<SideFit> | null | undefined, D: number): SideParams {
  if (!f || f.sides !== 'slatted') return {};
  const n = Math.max(1, Math.min(8, f.slatsPerSide ? f.slatsPerSide - 2 : 2));
  const w = Math.max(SLATS.widthMin, Math.min(SLATS.widthMax, r5(D / (n + 2 + (n + 1) / 2))));
  const gap = Math.max(SLATS.gapMin, Math.min(SLATS.gapMax, r5(w / 2)));
  return { sideStyle: 'slatted', slatWidth: w, slatGap: gap };
}
